import React, {useCallback, useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useAuth} from "../../../context/authContext";

import {Button, Col, Container, Form, InputGroup, Modal, Row, Spinner} from "react-bootstrap";
import Modals from "../../../components/modals";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import {
    AddMemberToUser,
    getPatients,
    hideAlert,
    hidePatientsFound,
    hidePopulationSearchForm,
    onChangeField,
    searchFromInputText,
    showPatient,
    showPopulationSearchForm
} from "../../../actions/populationSearch/populationSearchActions";
import PatientRow from "../../quickList/components/PatientRow";
import axios from "../../../utils/apiClient";
import ScheduledAppointment from "../../quickList/components/modals/scheduleAppointment";
import ChangeStatusModal from "../../quickList/components/modals/changeStatus";
import ResetStatusModal from "../../quickList/components/modals/resetStatus";

const componentName = "PopulationSearch";

const isEmpty = require("is-empty");

const AUTH_POPULATION_SEARCH_ACCESS = "AUTH_POPULATION_SEARCH_ACCESS";

const buildVisitGapsUrl = (patientId, action, careGapId) => {
    const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
    const params = [
        `patientId=${encodeURIComponent(patientId ?? "")}`,
        `action=${encodeURIComponent(action ?? "")}`,
    ];

    if (careGapId !== undefined) {
        params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
    }

    return `${baseUrl}/v1/patients/getVisitGaps?${params.join("&")}`;
};

const buildUpdateVisitGapsUrl = (patientId, action, careGapId) => {
    const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
    const params = [
        `patientId=${encodeURIComponent(patientId ?? "")}`,
        `action=${encodeURIComponent(action ?? "")}`,
    ];

    if (careGapId !== undefined) {
        params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
    }

    return `${baseUrl}/v1/patients/updateVisitGaps?${params.join("&")}`;
};

const resolveCareGapId = (patient) => {
    if (!patient) return null;

    const addCandidate = (collection, value) => {
        if (value === undefined || value === null) return;
        const normalized = typeof value === "string" ? value.trim() : value;
        if (normalized !== "" && normalized !== null) {
            collection.push(normalized);
        }
    };

    const candidates = [];

    addCandidate(candidates, patient.nextVisitId);
    addCandidate(candidates, patient.nextVisitID);
    addCandidate(candidates, patient.nextVisitGapId);
    addCandidate(candidates, patient.nextVisitGapID);

    addCandidate(candidates, patient.careGapId);
    addCandidate(candidates, patient.careGapID);
    addCandidate(candidates, patient.patientGapId);
    addCandidate(candidates, patient.patientGapID);

    if (Array.isArray(patient.careGapIds) && patient.careGapIds.length) {
        addCandidate(candidates, patient.careGapIds[0]);
    } else if (typeof patient.careGapIds === "string" && patient.careGapIds.length) {
        addCandidate(candidates, patient.careGapIds.split(",")[0]);
    }

    if (Array.isArray(patient.patientGapIds) && patient.patientGapIds.length) {
        addCandidate(candidates, patient.patientGapIds[0]);
    } else if (typeof patient.patientGapIds === "string" && patient.patientGapIds.length) {
        addCandidate(candidates, patient.patientGapIds.split(",")[0]);
    }

    return candidates.find((id) => id !== undefined && id !== null && id !== "") || null;
};

const PopulationSearch = () => {
    const { searchText, addedUser } = useSelector(state => state.populationSearch);
    const state = useSelector(state => state.populationSearch);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const auth = useAuth();
    const [selectedPatient, setSelectedPatient] = useState();
    const [showPatientDetails, setShowPatientDetails] = useState(false);
    const [activeAction, setActiveAction] = useState(null);
    const [visitGaps, setVisitGaps] = useState({ dueNowVisits: [], dueSoonVisits: [] });
    const [visitGapSelections, setVisitGapSelections] = useState({});
    const [visitGapsLoading, setVisitGapsLoading] = useState(false);
    const [visitGapsError, setVisitGapsError] = useState(null);
    const [visitGapsSubmitting, setVisitGapsSubmitting] = useState(false);
    const [changeStatusFormError, setChangeStatusFormError] = useState(null);
    const [resetStatusSubmitting, setResetStatusSubmitting] = useState(false);
    const [resetStatusSubmitError, setResetStatusSubmitError] = useState(null);
    const [textToShow, settextToShow] = useState('');

    const resetChangeStatusState = useCallback(() => {
        setVisitGaps({ dueNowVisits: [], dueSoonVisits: [] });
        setVisitGapSelections({});
        setVisitGapsLoading(false);
        setVisitGapsError(null);
        setVisitGapsSubmitting(false);
        setChangeStatusFormError(null);
    }, []);

    const resetResetStatusState = useCallback(() => {
        setResetStatusSubmitting(false);
        setResetStatusSubmitError(null);
    }, []);

    const loadVisitGaps = useCallback(async (patient) => {
        if (!patient?.id) {
            return;
        }

        setVisitGapsLoading(true);
        setVisitGapsError(null);

        try {
            const careGapId = resolveCareGapId(patient);
            const url = buildVisitGapsUrl(patient.id, "CHANGE_STATUS", careGapId);

            const normalizedPrimaryCareGapId =
                careGapId === undefined || careGapId === null ? null : String(careGapId).trim();

            const response = await axios.get(url, {
                headers: { "Content-Type": "application/json" },
            });

            const dueNowVisits = response?.data?.dueNowVisits || [];
            const dueSoonVisits = response?.data?.dueSoonVisits || [];

            const initialSelections = {};
            const primeSelections = (visits) => {
                if (!Array.isArray(visits)) return;
                visits.forEach((visit) => {
                    if (!visit?.careGapId) return;
                    const visitCareGapId = String(visit.careGapId).trim();
                    const shouldSelectByDefault =
                        !!visit.selected ||
                        (!!normalizedPrimaryCareGapId &&
                            normalizedPrimaryCareGapId.length &&
                            normalizedPrimaryCareGapId === visitCareGapId);
                    initialSelections[visit.careGapId] = {
                        careGapId: visit.careGapId,
                        selected: shouldSelectByDefault,
                        statusEnum: visit.statusEnum && visit.statusEnum !== "OPEN" ? visit.statusEnum : "",
                    };
                });
            };

            primeSelections(dueNowVisits);
            primeSelections(dueSoonVisits);

            setVisitGaps({ dueNowVisits, dueSoonVisits });
            setVisitGapSelections(initialSelections);
        } catch (error) {
            console.error("Failed to load visit gaps:", error);
            const message =
                error?.response?.data?.message || error?.message || "Unable to load visit gaps.";
            setVisitGapsError(message);
            setVisitGaps({ dueNowVisits: [], dueSoonVisits: [] });
            setVisitGapSelections({});
        } finally {
            setVisitGapsLoading(false);
        }
    }, []);

    const updateVisitGapSelection = useCallback((careGapId, updates) => {
        if (!careGapId) return;
        setChangeStatusFormError(null);
        setVisitGapSelections((prev) => {
            const existing = prev[careGapId] || { careGapId, selected: false, statusEnum: "" };
            return {
                ...prev,
                [careGapId]: {
                    ...existing,
                    ...updates,
                },
            };
        });
    }, []);

    const openAction = useCallback((action, patient) => {
        if (!patient) return;
        setSelectedPatient(patient);
        setActiveAction(action);
        resetResetStatusState();

        if (action === "changestatus") {
            resetChangeStatusState();
            loadVisitGaps(patient);
        }
    }, [loadVisitGaps, resetChangeStatusState, resetResetStatusState]);

    const closeAction = useCallback(() => {
        if (activeAction === "changestatus") {
            resetChangeStatusState();
        }
        if (activeAction === "resetstatus") {
            resetResetStatusState();
        }
        setActiveAction(null);
    }, [activeAction, resetChangeStatusState, resetResetStatusState]);

    const handleSubmitChangeStatus = useCallback(async () => {
        if (visitGapsLoading || visitGapsSubmitting) return;

        const selections = Object.values(visitGapSelections).filter((selection) => selection.selected);

        if (!selections.length) {
            setChangeStatusFormError("Select at least one visit to update.");
            return;
        }

        const missingStatus = selections.find((selection) => !selection.statusEnum);
        if (missingStatus) {
            setChangeStatusFormError("Choose a status for each selected visit.");
            return;
        }

        if (!selectedPatient?.id) {
            setChangeStatusFormError("Patient information is missing.");
            return;
        }

        const payload = selections.map(({ careGapId, statusEnum, appointmentDate }) => ({
            careGapId,
            statusEnum,
            appointmentDate: appointmentDate || null,
        }));

        try {
            setVisitGapsSubmitting(true);
            setChangeStatusFormError(null);

            const url = buildUpdateVisitGapsUrl(
                selectedPatient.id,
                "CHANGE_STATUS",
                selections[0]?.careGapId ?? null
            );

            await axios.post(url, payload, {
                headers: { "Content-Type": "application/json" },
            });

            closeAction();
        } catch (error) {
            console.error("Failed to update visits:", error);
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Unable to update visits. Please try again.";
            setChangeStatusFormError(message);
        } finally {
            setVisitGapsSubmitting(false);
        }
    }, [visitGapsLoading, visitGapsSubmitting, visitGapSelections, selectedPatient, closeAction]);

    const handleSubmitResetStatus = useCallback(async () => {
        if (resetStatusSubmitting) return;

        const patientId = selectedPatient?.id;
        if (!patientId) {
            setResetStatusSubmitError("Patient information is missing.");
            return;
        }

        const careGapId = resolveCareGapId(selectedPatient);
        if (!careGapId) {
            setResetStatusSubmitError("Unable to determine the visit to update.");
            return;
        }

        const payload = [{
            careGapId,
            statusEnum: "OPEN",
            appointmentDate: null,
        }];

        try {
            setResetStatusSubmitting(true);
            setResetStatusSubmitError(null);

            const url = buildUpdateVisitGapsUrl(patientId, "CHANGE_STATUS", careGapId);

            await axios.post(url, payload, {
                headers: { "Content-Type": "application/json" },
            });

            closeAction();
        } catch (error) {
            console.error("Failed to reset status:", error);
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Unable to reset status. Please try again.";
            setResetStatusSubmitError(message);
        } finally {
            setResetStatusSubmitting(false);
        }
    }, [resetStatusSubmitting, selectedPatient, closeAction]);

    const submitPatientToUser = (patient) => {
        setSelectedPatient(patient);
        dispatch(AddMemberToUser(patient.id));
    }

    const getPatientKey = (patient, index) => {
        const key =
            patient?.id ??
            patient?.patientId ??
            patient?.patientID ??
            patient?.memberId;

        return key ?? `patient-${index}`;
    };

    useEffect(() => {
        if (addedUser === true) {
            dispatch(showPatient(history, selectedPatient, location));
        }
    }, [addedUser, dispatch, history, location, selectedPatient]);

    useEffect(() => {
        if (searchText !== '') {
            settextToShow(searchText)
        } else if (state?.searchForm?.lastName !== '' && state?.searchForm?.firstName !== '') {
            settextToShow(`${state.searchForm.firstName} ${state.searchForm.lastName}`)
        } else if (state?.searchForm?.memberId !== '') {
            settextToShow(state?.searchForm?.memberId)
        }
    }, [searchText, state])

    useEffect(() => {
        const unblock = history.block(() => {
            if (state.temporaryHidePSG) return true;
            if (state.isShowingForm || !isEmpty(state.patientsFound)) {
                dispatch(hidePopulationSearchForm())
                dispatch(hidePatientsFound())
                return false
            }
        })

        return () => {
            unblock()
        }
    }, [dispatch, history, state.isShowingForm, state.patientsFound, state.temporaryHidePSG])

    return (
        <>
            <div id={`${componentName}-search`} className="me-2">
                <div className="d-inline-block">
                    <Spinner animation="border" variant="secondary" size="sm" className="me-2" style={{ display: state.isShowingSpinner ? "block" : "none" }} />
                </div>
                <div className="d-inline-block">
                    <Form autoComplete="new-password" id="menuPatientSearch" onSubmit={(e) => { e.preventDefault(); }}>
                        <InputGroup>
                            <Form.Control
                                placeholder="Search all patients"
                                aria-describedby="basic-addon2"
                                name="searchText"
                                value={state.searchText}
                                onKeyDown={(e) => dispatch(searchFromInputText(e, state.searchText, auth))}
                                onChange={e => dispatch(onChangeField(e))}
                                onClick={e => dispatch(onChangeField(state.searchPlaceHolder, "searchText"))}
            
                                autoComplete="off"
                                id={`${componentName}-input`}
                                type="search"
                            />
                            <div className="d-inline-block" style={{ display: auth.hasAuthority(AUTH_POPULATION_SEARCH_ACCESS) ? "block" : "none" }}>
                                <Button hidden={!auth.hasAuthority(AUTH_POPULATION_SEARCH_ACCESS)} variant="link" id={`${componentName}-menuGlobalPatientSearch`} className="h-100 text-dark text-decoration-none" onClick={() => dispatch(showPopulationSearchForm())}>
                                    <i className="fa-regular fa-globe"></i>
                                </Button>
                            </div>
                        </InputGroup>
                    </Form>
                </div>
            </div>
            
            <Modal size="lg" show={state.isShowingForm && isEmpty(state.patientsFound)} onHide={() => dispatch(hidePopulationSearchForm())} centered>
                <Modal.Header>
                    <Modal.Title id={`${componentName}-populationSearchModalTitle`}>Search Equality Health Network</Modal.Title>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => dispatch(hidePopulationSearchForm())}></button>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label id={`${componentName}-firstNameText`}>First Name</Form.Label>
                                <input
                                    type="text"
                                    name="firstName"
                                    id={`${componentName}-firstNameValue`}
                                    value={state.searchForm.firstName}
                                    className="form-control text-capitalize"
                                    onChange={e => dispatch(onChangeField(e))}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label id={`${componentName}-lastNameText`}>Last Name</Form.Label>
                                <input
                                    type="text"
                                    name="lastName"
                                    id={`${componentName}-lastNameValue`}
                                    value={state.searchForm.lastName}
                                    className="form-control text-capitalize"
                                    onChange={e => dispatch(onChangeField(e))}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label id={`${componentName}-dateOfBirthText-1`}>Date of Birth</Form.Label>
                                <DatePicker
                                    name="dateOfBirthFirstOption"
                                    id={`${componentName}-dateOfBirthValue-1`}
                                    selected={state.searchForm.dateOfBirthFirstOption}
                                    onChange={e => dispatch(onChangeField(e, "dateOfBirthFirstOption"))}
                                    className="form-control"
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={1}>OR</Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label id={`${componentName}-memberIdText`}>Member Id</Form.Label>
                                <input
                                    type="text"
                                    name="memberId"
                                    id={`${componentName}-memberIdValue`}
                                    className="form-control text-capitalize"
                                    value={state.searchForm.memberId}
                                    onChange={e => dispatch(onChangeField(e))}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label id={`${componentName}-dateOfBirthText-2`}>Date of Birth</Form.Label>
                                <DatePicker
                                    name="dateOfBirthSecondOption"
                                    id={`${componentName}-dateOfBirthValue-2`}
                                    selected={state.searchForm.dateOfBirthSecondOption}
                                    onChange={e => dispatch(onChangeField(e, "dateOfBirthSecondOption"))}
                                    className="form-control"
                                    autoComplete="off"
                                    popperPlacement="top"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <p id={`${componentName}-instructionsWarning`} className="text-danger">By clicking "Search" below, I attest that I will access only those records that are required for the purposes of treatment and/or healthcare management of the patient.</p>
                            <p id={`${componentName}-instructionsNote`}><b>Note:</b> Users must capture a matching combination of Member ID and Date of Birth <b>OR</b> full Name and Date of Birth in order to access a member's record.</p>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button id={`${componentName}-searchButton`} variant="secondary" onClick={() => dispatch(hidePopulationSearchForm())}>
                        Cancel
                    </Button>
                    <Button id={`${componentName}-searchButton`} variant="primary" onClick={() => dispatch(getPatients(state.searchForm, auth))}>
                        Search
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modals
                show={!isEmpty(state.error)}
                onHide={() => {
                    dispatch(hideAlert());
                    if (state.doLogout) {
                        auth.logout();
                    }
                }}
                title="Search"
                titleid={`${componentName}-search`}
                size="lg"
                closeBtnId={`${componentName}-closeButton`}
                hideClickButton={true}
                hideFooter={false}
                body={<span>{state.error}</span>}
            />
            <Modal
                show={!isEmpty(state.patientsFound)}
                onHide={() => dispatch(hidePatientsFound())}
                size={"xl"}
                style={{ visibility: state.temporaryHidePSG ? 'hidden' : 'visible'}}
                backdrop={!state.temporaryHidePSG}
            >                {!isEmpty(state.patientsFound) && state.patientsFound.count > 0 && state.patientsFound.isPatientVisible && ([
                    <Modal.Header closeButton key={`${componentName}-patientModalHeader`} id={`${componentName}-patientModalHeader`}>
                        <Modal.Title id={`${componentName}-patientModalTitle`} className="w-100">
                            Search Results
                        </Modal.Title>
                    </Modal.Header>,
                    <Modal.Body key={"patientModalBody"}>
                        <div className="mb-3 fw-bold">
                            Showing Results for "{textToShow}"
                        </div>
                        {state?.patientsFound?.quickListResponseDTO?.patientCardDtos?.map((patient, index) => {
                            const patientKey = getPatientKey(patient, index);
                            return (
                                <div key={`${componentName}-patient-${patientKey}`} id={`${componentName}-patient-${index}`} className="cursor-p">
                                    <PatientRow
                                        patient={patient}
                                        quickListType="ALL_PATIENTS"
                                        idx={index}
                                        isPatientSelected={() => false}
                                        setSelectedPatient={setSelectedPatient}
                                        setShowPatientDetails={setShowPatientDetails}
                                        showPatientDetails={showPatientDetails}
                                        pageTitle="my title"
                                        onAction={openAction}
                                        isGlobalSearch={true}
                                        hideResetStatusAction={true}
                                    />
                                </div>
                            )
                        })}
                    </Modal.Body>
                ])}

                {!isEmpty(state.patientsFound) && state.patientsFound.count > 0 && !state.patientsFound.isPatientVisible && ([
                    <Modal.Header closeButton key={"patientModalHeader"}>
                        <Modal.Title className="w-100">
                            Search Results
                        </Modal.Title>
                    </Modal.Header>,
                    <Modal.Body key={"patientModalBody"}>
                        <div className="mb-3 fw-bold">
                            Showing Results for "{textToShow}"
                        </div>
                        <Container>
                            {state.patientsFound.quickListResponseDTO.patientCardDtos.map((patient, index) => {
                                const patientKey = getPatientKey(patient, index);
                                return (
                                    <Row key={`${componentName}-patient-${patientKey}`} className="cursor-p">
                                        <PatientRow
                                            patient={patient}
                                            quickListType="ALL_PATIENTS"
                                            idx={index}
                                            isPatientSelected={() => false}
                                            setSelectedPatient={setSelectedPatient}
                                            setShowPatientDetails={setShowPatientDetails}
                                            showPatientDetails={showPatientDetails}
                                            pageTitle="my title"
                                            onAction={openAction}
                                            isGlobalSearch={true}
                                            hideResetStatusAction={true}
                                        />

                                        <Col>
                                            <p id={`${componentName}-patientNote-${index}`}><strong>Note:</strong> This patient is attributed to {patient.originatingOrganization}. Clicking "Yes" will add this patient to your Roster temporarily for 14 days, after which the patient will no longer be on your roster. Would you like to add the patient to your Roster?</p>
                                            <Modal.Footer>
                                                <Button id={`${componentName}-patientAddToWorklist-${index}`} variant="primary" onClick={() => submitPatientToUser(patient)}>
                                                    Yes
                                                </Button>
                                                <Button id={`${componentName}-patientDontAddToWorklist-${index}`} variant="secondary" onClick={() => dispatch(hidePatientsFound())}>
                                                    No
                                                </Button>
                                            </Modal.Footer>
                                        </Col>
                                    </Row>
                                )
                            })}
                        </Container>
                    </Modal.Body>
                ])}
            </Modal>
            {selectedPatient && ["schedule", "reschedule", "unschedule"].includes(activeAction) && (
                <ScheduledAppointment
                    show={true}
                    onHide={closeAction}
                    patient={selectedPatient}
                    appointmentAction={activeAction}
                />
            )}
            {activeAction === "changestatus" && (
                <ChangeStatusModal
                    show={true}
                    onHide={closeAction}
                    onSubmit={handleSubmitChangeStatus}
                    patient={selectedPatient}
                    visitGaps={visitGaps}
                    visitGapSelections={visitGapSelections}
                    visitGapsLoading={visitGapsLoading}
                    visitGapsError={visitGapsError}
                    changeStatusFormError={changeStatusFormError}
                    visitGapsSubmitting={visitGapsSubmitting}
                    updateVisitGapSelection={updateVisitGapSelection}
                />
            )}
            {activeAction === "resetstatus" && (
                <ResetStatusModal
                    show={true}
                    onHide={closeAction}
                    onSubmit={handleSubmitResetStatus}
                    patient={selectedPatient}
                    submitting={resetStatusSubmitting}
                    submitError={resetStatusSubmitError}
                />
            )}
        </>
    );
};

export default PopulationSearch;
